import cmsMeasuresData from '../data/2025-mips-data/cms_2025_mips_measures.json';
import vbcaBenchmarksData from '../data/2025-mips-data/vbca_2025_quality_benchmarks.json';
import activeMeasuresData from '../data/2025-mips-data/vbca_active_measures.json';
import strataConfigData from '../data/2025-mips-data/vbca_2025_strata_measures.json';
// 2026 datasets (to add a future year: drop files in src/data/<year>-mips-data/,
// import them here, and add an entry to MipsDataService.DATASETS below).
import cmsMeasuresData2026 from '../data/2026-mips-data/cms_2026_mips_measures.json';
import vbcaBenchmarksData2026 from '../data/2026-mips-data/vbca_2026_quality_benchmarks.json';
import activeMeasuresData2026 from '../data/2026-mips-data/vbca_active_measures.json';
import strataConfigData2026 from '../data/2026-mips-data/vbca_2026_strata_measures.json';
// MVP (Value Pathways) catalog — year-keyed like everything else. 2025 has no MVP
// dataset (the module is inert for that year by design).
import mvpsData2026 from '../data/2026-mips-data/cms_2026_mvps.json';
// Specialty crosswalk (year-agnostic file; its mvpSpecialtyToCodes map is validated
// against the viewing year's MVP catalog at load — see runMvpAssertions).
import specialtyCrosswalkData from '../data/vbca_specialty_crosswalk.json';

// Type definitions based on actual data structure
export interface CmsMeasure {
  measureId: string;
  title: string;
  description: string;
  category: string;
  measureType?: string;
  isOutcomeMeasure?: boolean;
  metricType?: string;
  isHighPriority?: boolean;
  isInverse?: boolean;
  primarySteward?: string;
  firstPerformanceYear?: number;
  lastPerformanceYear?: number | null;
  eMeasureId?: string | null;
  nqfId?: string | null;
  nqfEMeasureId?: string | null;
  subcategoryId?: string;
  allowedPrograms?: string[];
  submissionMethods?: string[];
  measureSets?: string[];
  measureSpecification?: {
    electronicHealthRecord?: string;
    claims?: string;
    registry?: string;
    /** PI / cost measures often use a single `default` URL */
    default?: string;
  };
  isRegistryMeasure?: boolean;
  isRiskAdjusted?: boolean;
  icdImpacted?: any[];
  isClinicalGuidelineChanged?: boolean;
  isIcdImpacted?: boolean;
  clinicalGuidelineChanged?: any[];
  companionMeasureId?: string[];
  strata?: Array<{
    name?: string;
    description: string;
  }>;
  overallAlgorithm?: string;
  isSevenPointCapRemoved?: boolean;
  sevenPointCapRemoved?: any[];
  allowedVendors?: string[];
}

export interface VbcaBenchmark {
  measureId: string;
  submissionMethods: string;
  isInverse: string;
  cmsAverage: string;
  decile_1: string;
  decile_2: string;
  decile_3: string;
  decile_4: string;
  decile_5: string;
  decile_6: string;
  decile_7: string;
  decile_8: string;
  decile_9: string;
  decile_10: string;
  toppedOut: string;
  sevenPointCap: string;
  comments: string;
  scoringFloor: string; // "5_Points", "7_Points", or "N/A"
}

export interface DecileRange {
  from: number;
  to: number;
  points: number;
}

// Strata data for individual stratum
export interface StrataData {
  name: string;
  description: string;
  ip: number;  // Initial Population
  ex: number;  // Exclusions
  den: number; // Denominator
  met: number; // Numerator Met
  exc: number; // Numerator Exception
  not: number; // Numerator Not Met
}

// Strata configuration from vbca_2025_strata_measures.json
export interface StrataConfig {
  measureId: string;
  submissionMethods: string;
  strataCount: string;
  completeRate: string;
  performanceRate: string;
}

// Combined measure data with specific submission method
export interface MeasureWithSubmissionMethod {
  // From CMS measure
  measureId: string;
  title: string;
  description: string;
  category: string;
  measureType?: string;
  isOutcomeMeasure?: boolean;
  metricType?: string;
  isHighPriority?: boolean;
  primarySteward?: string;
  measureSets?: string[];
  
  // From specific benchmark
  submissionMethod: string; // MIPS CQM, QCDR, or eCQM
  isInverse: boolean;
  cmsAverage: number | null;
  deciles: DecileRange[];
  toppedOut: boolean;
  sevenPointCap: boolean;
  scoringFloor: string;
  autoPts: boolean; // Calculated from scoringFloor
  
  // Specification link based on submission method
  specificationLink?: string;
}

// One MVP (Value Pathway) as published by CMS (cms_{year}_mvps.json)
export interface MvpDefinition {
  mvpId: string;                       // canonical 4-digit form, e.g. 'M0001', 'G0055'
  clinicalTopic: string;
  title: string;
  description: string;
  specialtiesMostApplicableTo: string[];
  clinicalTopics: string;
  qualityMeasureIds: string[];
  iaMeasureIds: string[];
  costMeasureIds: string[];
  foundationPiMeasureIds: string[];
  foundationQualityMeasureIds: string[]; // population health: 479 / 484
  administrativeClaimsMeasureIds: string[];
  hasCahps: boolean;
  hasOutcomeAdminClaims: boolean;
}

// vbca_specialty_crosswalk.json shape (the two load-bearing maps)
export interface SpecialtyCrosswalk {
  canonicalSpecialties: Record<string, { description: string; taxonomyCodes: string[]; dacAliases: string[] }>;
  dacPrimarySpecialtyToCodes: Record<string, unknown>; // dormant (DAC dropped 2026-08-06)
  mvpSpecialtyToCodes: Record<string, { medicareSpecialtyCodes?: string[]; codes?: string[]; isUmbrella?: boolean }>;
}

/**
 * Normalize an MVP id to the canonical 4-digit form (owner-confirmed 2026-08-06:
 * `M0001`, not the guide's `M001` typo). Applies to M-prefixed ids only.
 */
export function normalizeMvpId(id: string): string {
  const m = /^([MG])(\d+)$/.exec((id || '').trim().toUpperCase());
  if (!m) return (id || '').trim();
  return `${m[1]}${m[2].padStart(4, '0')}`;
}

export class MipsDataService {
  private static instance: MipsDataService;
  private cmsMeasures: Map<string, CmsMeasure> = new Map();
  private vbcaBenchmarks: Map<string, VbcaBenchmark[]> = new Map();
  private activeMeasureIds: Set<string> = new Set();
  private strataConfigs: Map<string, StrataConfig[]> = new Map();
  private mvpCatalog: Map<string, MvpDefinition> = new Map();
  private mvpAssertionsRunFor: number | null = null;
  private activeYear: number = 2025;

  // Year-keyed raw datasets. Add future years here after importing their files.
  // `mvps` is optional: years without an MVP dataset (2025) keep the MVP module inert.
  private static readonly DATASETS: Record<number, {
    cmsMeasures: unknown; benchmarks: unknown; activeMeasures: { measureId: string[] }; strata: unknown;
    mvps?: unknown;
  }> = {
    2025: { cmsMeasures: cmsMeasuresData, benchmarks: vbcaBenchmarksData, activeMeasures: activeMeasuresData, strata: strataConfigData },
    2026: { cmsMeasures: cmsMeasuresData2026, benchmarks: vbcaBenchmarksData2026, activeMeasures: activeMeasuresData2026, strata: strataConfigData2026, mvps: mvpsData2026 },
  };

  private constructor() {
    this.loadData(this.activeYear);
  }

  static getInstance(): MipsDataService {
    if (!MipsDataService.instance) {
      MipsDataService.instance = new MipsDataService();
    }
    return MipsDataService.instance;
  }

  /** Current performance year whose static reference data is loaded. */
  getActiveYear(): number {
    return this.activeYear;
  }

  /**
   * Switch the active performance year and reload its static reference data
   * (measures, benchmarks, active list, strata). Falls back to the most recent
   * available year <= requested if the requested year has no dataset.
   */
  setActiveYear(year: number): void {
    if (this.activeYear === year && this.cmsMeasures.size > 0) {
      return; // already loaded
    }
    const available = Object.keys(MipsDataService.DATASETS).map(Number);
    let resolved = year;
    if (!MipsDataService.DATASETS[year]) {
      const leq = available.filter(y => y <= year).sort((a, b) => b - a);
      resolved = leq.length > 0 ? leq[0] : Math.max(...available);
      console.warn(`[MipsDataService] No dataset for year ${year}; using ${resolved}`);
    }
    this.activeYear = resolved;
    this.loadData(resolved);
  }

  private loadData(year: number) {
    const ds = MipsDataService.DATASETS[year] || MipsDataService.DATASETS[2025];

    // Reset maps before (re)loading the selected year
    this.cmsMeasures.clear();
    this.vbcaBenchmarks.clear();
    this.activeMeasureIds.clear();
    this.strataConfigs.clear();

    // Load active measure IDs
    ds.activeMeasures.measureId.forEach((id: string) => {
      this.activeMeasureIds.add(id);
    });

    // Load CMS measures
    (ds.cmsMeasures as CmsMeasure[]).forEach(measure => {
      this.cmsMeasures.set(measure.measureId, measure);
    });

    // Load and group VBCA benchmarks by measureId
    // Filter out Claims and Medicare CQM benchmarks (not used in MIPS registries)
    (ds.benchmarks as VbcaBenchmark[]).forEach(benchmark => {
      // Skip Claims and Medicare CQM benchmarks
      if (benchmark.submissionMethods === 'Claims' || benchmark.submissionMethods === 'Medicare CQM') {
        return;
      }
      
      const existing = this.vbcaBenchmarks.get(benchmark.measureId) || [];
      existing.push(benchmark);
      this.vbcaBenchmarks.set(benchmark.measureId, existing);
    });

    // Load strata configurations
    (ds.strata as StrataConfig[]).forEach(config => {
      const existing = this.strataConfigs.get(config.measureId) || [];
      existing.push(config);
      this.strataConfigs.set(config.measureId, existing);
    });

    // Load the MVP catalog (years without one keep the MVP module inert)
    this.mvpCatalog.clear();
    if (ds.mvps) {
      (ds.mvps as MvpDefinition[]).forEach(mvp => {
        this.mvpCatalog.set(normalizeMvpId(mvp.mvpId), { ...mvp, mvpId: normalizeMvpId(mvp.mvpId) });
      });
      this.runMvpAssertions(year);
    }
  }

  /**
   * Dev-only consistency assertions between the MVP catalog, the CMS measures file,
   * and the specialty crosswalk (IMPLEMENTATION_PLAN.md Phase A item 3). Catches a
   * bad/stale re-download of any of the three files. console.warn only — never throws.
   */
  private runMvpAssertions(year: number): void {
    if (!import.meta.env?.DEV || this.mvpAssertionsRunFor === year) return;
    this.mvpAssertionsRunFor = year;
    const xwalk = specialtyCrosswalkData as unknown as SpecialtyCrosswalk;
    const problems: string[] = [];

    this.mvpCatalog.forEach(mvp => {
      // (a) every quality measure id exists in the CMS measures file
      mvp.qualityMeasureIds.forEach(qid => {
        const m = this.cmsMeasures.get(qid);
        if (!m) {
          problems.push(`MVP ${mvp.mvpId}: quality measure ${qid} not in cms_${year}_mips_measures.json`);
        } else if (m.allowedPrograms && !m.allowedPrograms.includes(mvp.mvpId)) {
          // (b) allowedPrograms should encode the same membership (redundancy as an asset)
          problems.push(`MVP ${mvp.mvpId}: measure ${qid} allowedPrograms lacks ${mvp.mvpId}`);
        }
      });
      // (c) every specialty string resolves in the crosswalk (advisory data — warn only)
      mvp.specialtiesMostApplicableTo.forEach(s => {
        if (!xwalk.mvpSpecialtyToCodes?.[s]) {
          problems.push(`MVP ${mvp.mvpId}: specialty "${s}" missing from vbca_specialty_crosswalk.json mvpSpecialtyToCodes`);
        }
      });
    });

    if (problems.length > 0) {
      console.warn(`[MipsDataService] MVP catalog assertions (${year}): ${problems.length} issue(s)`, problems.slice(0, 20));
    } else {
      console.log(`[MipsDataService] MVP catalog assertions (${year}): clean (${this.mvpCatalog.size} MVPs)`);
    }
  }

  /** Does the given (or active) year have an MVP dataset? 2025 = false by design. */
  hasMvpDataset(year?: number): boolean {
    const y = year ?? this.activeYear;
    return !!MipsDataService.DATASETS[y]?.mvps;
  }

  // All MVPs for a performance year, sorted by id. Empty array when the year has no dataset.
  getMvps(year?: number): MvpDefinition[] {
    if (year != null) this.setActiveYear(year);
    return [...this.mvpCatalog.values()].sort((a, b) => a.mvpId.localeCompare(b.mvpId));
  }

  getMvp(mvpId: string, year?: number): MvpDefinition | undefined {
    if (year != null) this.setActiveYear(year);
    return this.mvpCatalog.get(normalizeMvpId(mvpId));
  }

  getSpecialtyCrosswalk(): SpecialtyCrosswalk {
    return specialtyCrosswalkData as unknown as SpecialtyCrosswalk;
  }

  // Get only active measures. Pass `year` to read a specific performance year's
  // active list (race-free — switches the loaded dataset synchronously first).
  getActiveMeasures(year?: number): CmsMeasure[] {
    if (year != null) this.setActiveYear(year);
    const activeMeasures: CmsMeasure[] = [];
    
    this.activeMeasureIds.forEach(measureId => {
      const measure = this.cmsMeasures.get(measureId);
      if (measure) {
        activeMeasures.push(measure);
      } else {
        console.warn(`Active measure ${measureId} not found in CMS measures data`);
      }
    });

    // Sort measures alphabetically by measureId (natural sort for proper numeric ordering)
    return activeMeasures.sort((a, b) => 
      a.measureId.localeCompare(b.measureId, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  // Get active measures by category (optionally for a specific performance year)
  getActiveMeasuresByCategory(category: string, year?: number): CmsMeasure[] {
    return this.getActiveMeasures(year).filter(measure => 
      measure.category.toLowerCase() === category.toLowerCase()
    );
  }

  // Get a specific active measure
  getActiveMeasure(measureId: string): CmsMeasure | undefined {
    if (!this.activeMeasureIds.has(measureId)) {
      return undefined;
    }
    return this.cmsMeasures.get(measureId);
  }

  // Get any CMS measure by ID (including non-active measures like PI measures)
  getCmsMeasure(measureId: string): CmsMeasure | undefined {
    return this.cmsMeasures.get(measureId);
  }

  // Get PI measure specification URL
  getPIMeasureSpecificationUrl(measureId: string): string | null {
    const measure = this.cmsMeasures.get(measureId);
    if (!measure?.measureSpecification) return null;
    
    // PI measures use 'default' key for specification URL
    const spec = measure.measureSpecification as { default?: string };
    return spec.default || null;
  }

  // Get benchmarks for a measure
  getBenchmarksForMeasure(measureId: string): VbcaBenchmark[] {
    return this.vbcaBenchmarks.get(measureId) || [];
  }

  // Get the primary benchmark (prefer MIPS CQM)
  getPrimaryBenchmark(measureId: string): VbcaBenchmark | undefined {
    const benchmarks = this.getBenchmarksForMeasure(measureId);
    
    return benchmarks.find(b => b.submissionMethods === 'MIPS CQM') ||
           benchmarks.find(b => b.submissionMethods === 'Claims') ||
           benchmarks.find(b => b.submissionMethods === 'eCQM') ||
           benchmarks.find(b => b.submissionMethods === 'Medicare CQM') ||
           benchmarks[0];
  }

  // Parse decile ranges from a specific benchmark
  private parseDecilesFromBenchmark(benchmark: VbcaBenchmark): DecileRange[] {
    const deciles: DecileRange[] = [];
    const isInverse = benchmark.isInverse === 'TRUE';

    // Parse each decile
    for (let i = 1; i <= 10; i++) {
      const decileKey = `decile_${i}` as keyof VbcaBenchmark;
      const decileValue = benchmark[decileKey] as string;

      if (!decileValue || decileValue === '--') continue;

      // Parse ranges like "99.00 - 90.01" or "<= 10.00"
      if (decileValue.includes(' - ')) {
        const [upper, lower] = decileValue.split(' - ').map(v => parseFloat(v));
        deciles.push({
          from: lower,
          to: upper,
          points: isInverse ? (11 - i) : i
        });
      } else if (decileValue.startsWith('<=')) {
        const value = parseFloat(decileValue.substring(2).trim());
        deciles.push({
          from: 0,
          to: value,
          points: isInverse ? (11 - i) : i
        });
      } else if (decileValue.startsWith('>=')) {
        const value = parseFloat(decileValue.substring(2).trim());
        deciles.push({
          from: value,
          to: 100,
          points: isInverse ? (11 - i) : i
        });
      } else if (decileValue === '100') {
        deciles.push({
          from: 100,
          to: 100,
          points: isInverse ? (11 - i) : i
        });
      }
    }

    return deciles;
  }

  // Parse decile ranges from benchmark data (uses primary benchmark for backward compatibility)
  getDecilesForMeasure(measureId: string): DecileRange[] {
    const benchmark = this.getPrimaryBenchmark(measureId);
    if (!benchmark) return [];
    
    return this.parseDecilesFromBenchmark(benchmark);
  }

  // Calculate points for a performance rate
  calculatePoints(measureId: string, performanceRate: number): number {
    const deciles = this.getDecilesForMeasure(measureId);
    
    for (const decile of deciles) {
      if (performanceRate >= decile.from && performanceRate <= decile.to) {
        return decile.points;
      }
    }
    
    return 0;
  }

  // Get measure metadata for the app (backward compatibility)
  getMeasureMeta(measureId: string) {
    const measure = this.getActiveMeasure(measureId);
    const benchmark = this.getPrimaryBenchmark(measureId);
    
    if (!measure) {
      return {
        collectionType: 'Unknown',
        inverse: false,
        autoPts: false,
        cmsAvg: 0,
        cap7: false,
        toppedOut: false
      };
    }

    // Use collection type from benchmark (which has the correct categorization)
    // If no benchmark exists, we can't determine the collection type accurately
    const collectionType = benchmark?.submissionMethods || 'Unknown';

    // Parse CMS average, handling "--" values
    const cmsAvg = (() => {
      if (!benchmark?.cmsAverage || benchmark.cmsAverage === '--') return null;
      return parseFloat(benchmark.cmsAverage);
    })();

    return {
      collectionType,
      measureType: measure.measureType,
      inverse: measure.isInverse || benchmark?.isInverse === 'TRUE',
      // Bonus points come from scoringFloor, not measure type
      autoPts: benchmark?.scoringFloor === '5_Points' || benchmark?.scoringFloor === '7_Points',
      cmsAvg: cmsAvg,
      cap7: benchmark?.sevenPointCap === 'Yes',
      toppedOut: benchmark?.toppedOut === 'Yes'
    };
  }

  // Search active measures
  searchActiveMeasures(query: string): CmsMeasure[] {
    const lowerQuery = query.toLowerCase();
    return this.getActiveMeasures().filter(measure => 
      measure.measureId.toLowerCase().includes(lowerQuery) ||
      measure.title.toLowerCase().includes(lowerQuery) ||
      measure.description.toLowerCase().includes(lowerQuery)
    );
  }

  // Get active measure count
  getActiveMeasureCount(): number {
    return this.activeMeasureIds.size;
  }

  // Check if a measure is active
  isMeasureActive(measureId: string): boolean {
    return this.activeMeasureIds.has(measureId);
  }

  // Get all available categories from active measures
  getActiveCategories(): string[] {
    const categories = new Set<string>();
    this.getActiveMeasures().forEach(measure => {
      categories.add(measure.category);
    });
    return Array.from(categories);
  }

  // Get submission methods for a measure
  getSubmissionMethodsForMeasure(measureId: string): string[] {
    const benchmarks = this.getBenchmarksForMeasure(measureId);
    return [...new Set(benchmarks.map(b => b.submissionMethods))];
  }

  // Get active measures with missing benchmarks (for debugging)
  getActiveMeasuresWithMissingBenchmarks(): string[] {
    const missingBenchmarks: string[] = [];
    
    this.activeMeasureIds.forEach(measureId => {
      const benchmarks = this.getBenchmarksForMeasure(measureId);
      if (benchmarks.length === 0) {
        missingBenchmarks.push(measureId);
      }
    });
    
    return missingBenchmarks;
  }

  // Get the correct specification link based on submission method
  private getSpecificationLink(measure: CmsMeasure, submissionMethod: string): string | undefined {
    if (!measure.measureSpecification) return undefined;
    
    switch (submissionMethod) {
      case 'MIPS CQM':
      case 'QCDR':
      // Medicare CQM specs (the *SSP twins) are published under the registry
      // key in the CMS file (…_MedicareCQM.pdf).
      case 'Medicare CQM':
        return measure.measureSpecification.registry;
        
      case 'eCQM':
        return measure.measureSpecification.electronicHealthRecord;
        
      default:
        return undefined;
    }
  }

  /**
   * Specification PDF for ANY CMS measure in the active year's catalog, by
   * collection type. Unlike getMeasureWithSubmissionMethod this does not
   * require the id to be on the active list or to have a benchmark row —
   * the Medicare CQM twins (001SSP…) have neither in the MIPS registry but
   * do carry a spec link.
   */
  getMeasureSpecificationUrl(measureId: string, submissionMethod: string): string | undefined {
    const measure = this.cmsMeasures.get(measureId);
    if (!measure) return undefined;
    return this.getSpecificationLink(measure, submissionMethod)
      // Last resort: any published link for the measure.
      ?? measure.measureSpecification?.registry
      ?? measure.measureSpecification?.electronicHealthRecord;
  }

  // Get a measure with a specific submission method
  getMeasureWithSubmissionMethod(measureId: string, submissionMethod: string): MeasureWithSubmissionMethod | undefined {
    // Check if measure is active
    if (!this.activeMeasureIds.has(measureId)) {
      return undefined;
    }

    const measure = this.cmsMeasures.get(measureId);
    if (!measure) return undefined;

    // Find the specific benchmark for this submission method
    const benchmarks = this.getBenchmarksForMeasure(measureId);
    const benchmark = benchmarks.find(b => b.submissionMethods === submissionMethod);
    if (!benchmark) return undefined;

    // Parse deciles for this specific benchmark
    const deciles = this.parseDecilesFromBenchmark(benchmark);

    // Parse CMS average
    const cmsAverage = (!benchmark.cmsAverage || benchmark.cmsAverage === '--') 
      ? null 
      : parseFloat(benchmark.cmsAverage);

    return {
      // CMS measure data
      measureId: measure.measureId,
      title: measure.title,
      description: measure.description,
      category: measure.category,
      measureType: measure.measureType,
      isOutcomeMeasure: measure.isOutcomeMeasure,
      metricType: measure.metricType,
      isHighPriority: measure.isHighPriority,
      primarySteward: measure.primarySteward,
      measureSets: measure.measureSets,
      
      // Benchmark data
      submissionMethod: benchmark.submissionMethods,
      isInverse: measure.isInverse || benchmark.isInverse === 'TRUE',
      cmsAverage: cmsAverage,
      deciles: deciles,
      toppedOut: benchmark.toppedOut === 'Yes',
      sevenPointCap: benchmark.sevenPointCap === 'Yes',
      scoringFloor: benchmark.scoringFloor,
      autoPts: benchmark.scoringFloor === '5_Points' || benchmark.scoringFloor === '7_Points',
      
      // Specification link
      specificationLink: this.getSpecificationLink(measure, submissionMethod)
    };
  }

  // Get all available submission methods for active measures
  getActiveMeasuresWithAllSubmissionMethods(): MeasureWithSubmissionMethod[] {
    const results: MeasureWithSubmissionMethod[] = [];

    this.activeMeasureIds.forEach(measureId => {
      const benchmarks = this.getBenchmarksForMeasure(measureId);

      benchmarks.forEach(benchmark => {
        const measureData = this.getMeasureWithSubmissionMethod(measureId, benchmark.submissionMethods);
        if (measureData) {
          results.push(measureData);
        }
      });
    });

    // Sort by measureId for consistent alphabetical ordering
    return results.sort((a, b) => 
      a.measureId.localeCompare(b.measureId, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  // Get active measures grouped by submission method
  getActiveMeasuresBySubmissionMethod(): Map<string, MeasureWithSubmissionMethod[]> {
    const grouped = new Map<string, MeasureWithSubmissionMethod[]>();
    
    const allMeasures = this.getActiveMeasuresWithAllSubmissionMethods();
    
    allMeasures.forEach(measure => {
      const existing = grouped.get(measure.submissionMethod) || [];
      existing.push(measure);
      grouped.set(measure.submissionMethod, existing);
    });

    return grouped;
  }

  // Maps an application submission method (e.g., eCQM) to the one used in the strata config JSON (e.g., electronicHealthRecord)
  private mapSubmissionMethodToStrataConfig(submissionMethod: string): string {
    switch (submissionMethod) {
      case 'eCQM':
        return 'electronicHealthRecord';
      case 'MIPS CQM':
      case 'QCDR':
        return 'registry';
      // Other direct matches
      default:
        return submissionMethod;
    }
  }

  // Check if a measure is multi-strata for a specific submission method
  isMultiStrataMeasure(measureId: string, submissionMethod?: string): boolean {
    const configs = this.strataConfigs.get(measureId);
    if (!configs || configs.length === 0) return false;
    
    if (submissionMethod) {
      // Check for specific submission method after mapping the name
      const mappedSubmissionMethod = this.mapSubmissionMethodToStrataConfig(submissionMethod);
      const config = configs.find(c => c.submissionMethods === mappedSubmissionMethod);
      return config ? parseInt(config.strataCount) > 1 : false;
    }
    
    // If no submission method specified, check if any config has multiple strata
    return configs.some(c => parseInt(c.strataCount) > 1);
  }

  // Get strata configuration for a measure and submission method
  getStrataConfig(measureId: string, submissionMethod?: string): StrataConfig | null {
    const configs = this.strataConfigs.get(measureId);
    if (!configs || configs.length === 0) return null;
    
    if (submissionMethod) {
      // Return config for specific submission method after mapping the name
      const mappedSubmissionMethod = this.mapSubmissionMethodToStrataConfig(submissionMethod);
      return configs.find(c => c.submissionMethods === mappedSubmissionMethod) || null;
    }
    
    // Return first available config if no submission method specified
    return configs[0];
  }

  // Get strata definitions from CMS measure data
  getStrataDefinitions(measureId: string): Array<{name?: string; description: string}> {
    const measure = this.cmsMeasures.get(measureId);
    return measure?.strata || [];
  }

  // Check if a measure has custom strata (stratum ending with letters like _A, _B)
  isCustomStrataMeasure(measureId: string): boolean {
    // Custom strata measures follow the pattern: measureId_LETTER
    // For example: 118_A, 118_B
    // These measures always use weighted average for aggregation
    const customStrataPattern = new RegExp(`^${measureId}_[A-Z]$`, 'i');
    
    // Check if there are any measures in our data that match this pattern
    // This would typically be determined by looking at actual strata data,
    // but we can also check if the measure is configured with weighted average
    const config = this.getStrataConfig(measureId);
    
    // If configured with weighted average for both rates, likely a custom strata measure
    if (config && 
        config.performanceRate === 'Weighted Average' && 
        config.completeRate === 'Weighted Average') {
      return true;
    }
    
    return false;
  }

  // Get the algorithm for custom strata measures
  getCustomStrataAlgorithm(): string {
    return 'Weighted Average';
  }
}
